import { z } from 'zod';
import { PresetIdSchema, BrandContextSchema, QualityLevelSchema } from './generation.js';
export const MaskSessionStatusSchema = z.enum([
    'pending',
    'editing',
    'submitted',
    'generating',
    'awaiting_acceptance',
    // After the user clicks Accept on at least one iteration. Non-terminal:
    // they can paint a new mask and submit again, cycling back through
    // submitted -> generating -> awaiting_acceptance. The latest accepted
    // image is the one the agent receives once the session reaches 'finished'.
    'iterating',
    'completed',
    // Terminal state — reached either by clicking Finish in the editor or
    // by the tab-close beacon firing. The MCP /result poller treats this
    // identically to 'completed' for back-compat (both surface as the
    // legacy `status: 'completed'` 200 response).
    'finished',
    'expired',
]);
export const EditorSessionSchema = z.object({
    sessionId: z.string().uuid(),
    userId: z.string(),
    imageR2Key: z.string(),
    instruction: z.string(),
    preset: PresetIdSchema.optional(),
    brandContext: BrandContextSchema.optional(),
    status: MaskSessionStatusSchema,
    nonce: z.string().uuid(),
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    resultR2Key: z.string().optional(),
    // Snapshot of the result the user explicitly accepted on the most recent
    // Accept click. Points at a separate R2 key (`accepted-result.png`) that
    // is overwritten on each accept, so subsequent submits/generations don't
    // clobber it. This is what Finish (or the tab-close beacon) delivers to
    // the agent.
    acceptedResultR2Key: z.string().optional(),
    // Monotonic counter of how many times Accept has been clicked on this
    // session. Used by the UI to unlock the Finish button after the first
    // accept and by the server to validate finish-time invariants.
    iterationsAccepted: z.number().int().nonnegative().optional(),
    // Stamped when the session reaches 'finished'. Idempotent re-finishes
    // preserve the original timestamp.
    finishedAt: z.string().datetime().optional(),
    rating: z.number().int().min(1).max(5).optional(),
    tokenCost: z.number().optional(),
    remainingTokens: z.number().optional(),
    // Story 7.6: timestamp the user acceptance was persisted. Stored on the
    // record so concurrent/idempotent accepts return the SAME timestamp
    // (AC8 requires both callers receive byte-identical delivery payloads).
    acceptedAt: z.string().datetime().optional(),
});
export const MaskSubmissionSchema = z.object({
    sessionId: z.string().uuid(),
    mask_base64: z.string().min(1),
    instruction: z.string().min(1).max(500),
    idempotencyKey: z.string().uuid(),
    quality_hint: QualityLevelSchema.default('medium').optional(),
});
export const CreateEditSessionRequestSchema = z.object({
    image: z.string().max(10_000_000),
    instruction: z.string().max(500),
    preset: PresetIdSchema.optional(),
    brandContext: BrandContextSchema.optional(),
});
export const EditSessionResponseSchema = z.object({
    sessionId: z.string().uuid(),
    editorUrl: z.string().url(),
    expiresAt: z.string().datetime(),
});
export const MaskSubmissionResponseSchema = z.object({
    status: z.literal('completed'),
    imageUrl: z.string(),
    tokenCost: z.number(),
    remainingTokens: z.number(),
});
export const EditResultPollResponseSchema = z.union([
    z.object({
        status: z.enum([
            'pending',
            'editing',
            'submitted',
            'generating',
            'awaiting_acceptance',
            'iterating',
        ]),
        expiresAt: z.string().datetime(),
    }),
    z.object({
        status: z.literal('completed'),
        imageUrl: z.string().url(),
        tokenCost: z.number(),
        remainingTokens: z.number(),
    }),
    z.object({
        status: z.literal('expired'),
    }),
]);
export const AcceptEditRequestSchema = z.object({}).strict();
export const AcceptEditResponseSchema = z.object({
    // 'iterating' after the new state machine landed (Accept no longer
    // terminates the session). The browser uses this to unlock the Finish
    // button and reset the dock for the next iteration.
    status: z.literal('iterating'),
    imageUrl: z.string().url(),
    tokenCost: z.number(),
    remainingTokens: z.number(),
    acceptedAt: z.string().datetime(),
    iterationsAccepted: z.number().int().positive(),
});
export const FinishEditRequestSchema = z.object({}).strict();
export const FinishEditResponseSchema = z.object({
    status: z.literal('finished'),
    imageUrl: z.string().url(),
    tokenCost: z.number(),
    remainingTokens: z.number(),
    finishedAt: z.string().datetime(),
    iterationsAccepted: z.number().int().positive(),
});
//# sourceMappingURL=editor.js.map